import React, { useState, useCallback, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Paper,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Select,
  MenuItem,
  Link,
} from "@mui/material";
import { CloudUpload, Description, AutoAwesome } from "@mui/icons-material";
import {
  OperationLogService,
  GenerateFromOperationLogResponse,
  GenerateFromOperationLogRequest,
} from "../services/operationLogService";
import { TEXT_TEMPLATES, TEMPLATE_OPTIONS, TemplateType } from "../config/textTemplates";
import {
  OPERATION_LOG_SIZE_OVER_MESSAGE,
  OPERATION_LOG_SIZE_OVER_GUIDANCE_TEXT,
  OPERATION_LOG_SIZE_OVER_MANUAL_URL,
} from "../config/operationLogUpload";
import { is_operation_log_file_size_over_limit } from "../utils/operationLogFileSize";
import {
  is_operation_log_content,
  OPERATION_LOG_INVALID_MESSAGE,
  OPERATION_LOG_FORMAT_GUIDANCE,
} from "../utils/operationLogValidation";

interface OperationLogImportDialogProps {
  open: boolean;
  onClose: () => void;
  onResultsGenerated?: (result: GenerateFromOperationLogResponse) => void;
  onPreviewReady?: (
    preview: GenerateFromOperationLogResponse,
    request: GenerateFromOperationLogRequest
  ) => void;
  workspaceId: string;
  projectId: string;
  operationLogService: OperationLogService;
  // userToken removed - using Auth0 authentication
  /** グループ化用の View ID（Component パネル時は親 View の ID） */
  viewId?: string;
  viewName?: string;
  /** 対象ビュー/対象コンポーネントの表示名（パネルで選んだターゲット名） */
  scopeDisplayName?: string;
  /** 表示ラベルと任意 Path 欄の出し分け */
  scopeKind?: "view" | "component";
  /** 生成ターゲットの登録先 ID（View または Component） */
  registrationParentTestTargetId?: string;
  // モード: "target" = ターゲット生成のみ, "usecase" = ユースケースステップ生成のみ
  mode?: "target" | "usecase";
  // ユースケースステップ生成用のプロパティ
  useCaseId?: string;
  useCaseTitle?: string;
  useCaseDescription?: string;
  targetItemsCount?: number;
  appSettings?: any;
}

export const OperationLogImportDialog: React.FC<
  OperationLogImportDialogProps
> = ({
  open,
  onClose,
  onResultsGenerated,
  onPreviewReady,
  workspaceId,
  projectId,
  operationLogService,
  // userToken removed - using Auth0 authentication
  viewId,
  viewName,
  scopeDisplayName,
  scopeKind = "view",
  registrationParentTestTargetId,
  mode, // modeが未指定の場合はRadioGroupで選択可能
  useCaseId,
  useCaseTitle,
  useCaseDescription,
  targetItemsCount,
  appSettings,
}) => {
  console.log("🔵 OperationLogImportDialog rendered", {
    open,
    mode,
    workspaceId,
    projectId,
    viewId,
    viewName,
    operationLogService: !!operationLogService,
  });

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [additionalText, setAdditionalText] = useState<string>("");
  const [templateType, setTemplateType] = useState<TemplateType>("none");
  /** Component スコープ時の任意 path（空なら操作ログ全体を対象） */
  const [componentScopePath, setComponentScopePath] = useState("");
  const [generationMode, setGenerationMode] = useState<
    "both" | "targets_only" | "usecases_only"
  >(
    mode === "target"
      ? "targets_only"
      : mode === "usecase"
      ? "usecases_only"
      : "both"
  );

  /** プロジェクト全体対象（ServiceList経路）のときは操作ログ必須 */
  const operationLogRequired = mode === undefined;

  // ダイアログが開かれたときに状態をリセット
  useEffect(() => {
    if (open) {
      setSelectedFile(null);
      setLoading(false);
      setError(null);
      setDragOver(false);
      setTemplateType("none");
      setComponentScopePath("");

      // ユースケースモードの場合は、タイトルと説明をadditionalTextに設定
      if (mode === "usecase" && useCaseTitle && useCaseDescription) {
        const parts = [];
        if (useCaseTitle) {
          parts.push(`タイトル: ${useCaseTitle}`);
        }
        if (useCaseDescription) {
          parts.push(`説明: ${useCaseDescription}`);
        }
        setAdditionalText(parts.join('\n\n'));
      } else {
        setAdditionalText("");
      }

      // モードに応じて生成モードを設定
      setGenerationMode(
        mode === "target"
          ? "targets_only"
          : mode === "usecase"
          ? "usecases_only"
          : "both"
      );
    }
  }, [open, mode, useCaseTitle, useCaseDescription]);

  // ファイル選択処理
  const handleFileSelect = useCallback((file: File) => {
    if (file.type !== "application/json" && !file.name.endsWith(".json")) {
      setError("JSONファイルを選択してください");
      return;
    }
    if (is_operation_log_file_size_over_limit(file)) {
      setError(OPERATION_LOG_SIZE_OVER_MESSAGE);
      setSelectedFile(null);
      return;
    }
    setSelectedFile(file);
    setError(null);
  }, []);

  // ファイルドロップ処理
  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);

      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        handleFileSelect(files[0]);
      }
    },
    [handleFileSelect]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  // ファイル入力変更処理
  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        handleFileSelect(files[0]);
      }
    },
    [handleFileSelect]
  );

  // ファイル読み込み処理
  const readFileAsJson = (file: File): Promise<any> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const json = JSON.parse(e.target?.result as string);
          resolve(json);
        } catch (error) {
          reject(new Error("JSONファイルの解析に失敗しました"));
        }
      };
      reader.onerror = () =>
        reject(new Error("ファイルの読み込みに失敗しました"));
      reader.readAsText(file);
    });
  };

  // テストターゲット生成処理
  const handleGenerate = async () => {
    console.log("🚀 handleGenerate called");
    console.log("📋 Parameters:", {
      selectedFile: selectedFile?.name,
      additionalText,
      workspaceId,
      projectId,
      viewId,
      mode: generationMode,
      operationLogService: !!operationLogService,
    });

    // プロジェクト全体対象のときは操作ログ必須
    if (operationLogRequired && !selectedFile) {
      console.log("❌ Validation failed: Operation log required but no file");
      setError("操作ログファイルをアップロードしてください。");
      return;
    }

    // 画面指定時は操作ログまたは登録対象情報のいずれかが必要
    if (!operationLogRequired && !selectedFile && !additionalText.trim()) {
      console.log("❌ Validation failed: No file or text provided");
      setError(
        "操作ログファイルまたは登録対象情報のいずれかを入力してください"
      );
      return;
    }

    if (!workspaceId || !projectId) {
      console.log("❌ Validation failed: Missing workspaceId or projectId");
      setError("ワークスペースIDとプロジェクトIDが必要です");
      return;
    }

    if (
      selectedFile &&
      is_operation_log_file_size_over_limit(selectedFile)
    ) {
      setError(OPERATION_LOG_SIZE_OVER_MESSAGE);
      return;
    }

    console.log("✅ Validation passed, starting generation...");
    setLoading(true);
    setError(null);

    try {
      // ファイルが選択されている場合は読み込み、なければnull
      const operationLogData = selectedFile
        ? await readFileAsJson(selectedFile)
        : null;

      console.log("📄 Operation log data:", operationLogData ? "loaded" : "null");

      if (operationLogData !== null && !is_operation_log_content(operationLogData)) {
        setError(
          `${OPERATION_LOG_INVALID_MESSAGE}${OPERATION_LOG_FORMAT_GUIDANCE}`
        );
        setLoading(false);
        return;
      }

      // NOTE: viewIdの使用条件について
      // - mode="target": ItemSpecificationPanelから開いた「AIでターゲットを生成」ダイアログ
      //   特定のビューが対象（viewIdを使用）
      // - mode="usecase": UseCaseEditModalから開いた「AIでステップを生成」ダイアログ
      //   特定のビューが対象（viewIdを使用）
      // - mode=undefined: TestTargetManagerから開いた「AIで生成」ダイアログ
      //   全ビューが対象（viewIdを無視、generationModeで選択可能）
      // 
      // generationMode="targets_only"を選択しても、mode="target"でない限り
      // viewIdは使用されない（全ビューが対象）
      const effectiveViewId = mode === "target" || mode === "usecase" ? viewId : undefined;
      
      console.log("📋 Request payload preparation:", {
        mode,
        viewId,
        viewName,
        effectiveViewId,
        generationMode,
        note: mode === undefined 
          ? "全ビューが対象（generationModeの選択に関係なくviewIdは無視）"
          : mode === "target"
          ? "特定ビューが対象（ItemSpecificationPanelから開いた場合）"
          : "特定ビューが対象（UseCaseEditModalから開いた場合）",
      });

      const scopePathTrimmed = componentScopePath.trim();
      const requestPayload: GenerateFromOperationLogRequest = {
        workspaceId,
        projectId,
        operationLogData,
        viewId: effectiveViewId,
        registrationParentTestTargetId:
          registrationParentTestTargetId ?? undefined,
        scopePathFilter:
          mode === "target" && scopeKind === "component" && scopePathTrimmed
            ? scopePathTrimmed
            : undefined,
        additionalText: additionalText || undefined,
        templateType: mode === "target" ? templateType : undefined, // ターゲット生成モードの場合のみテンプレートIDを送信
        mode: generationMode,
        // 特定画面の個別ユースケース作成時のみ既存ユースケース情報を無視
        // (viewIdが指定されている && mode="usecase")
        ignoreExistingUsecases: mode === "usecase" && !!effectiveViewId,
        appSettings,
      };

      // プレビュー先行フローが指定されている場合はpreviewを呼び出す
      if (onPreviewReady) {
        console.log("🔄 Calling operationLogService.previewFromOperationLog...");
        const previewResult = await operationLogService.previewFromOperationLog(
          requestPayload
        );
        console.log("✅ プレビュー生成結果:", previewResult);
        onPreviewReady(previewResult, requestPayload);
        onClose();
        return;
      }

      console.log("🔄 Calling operationLogService.generateFromOperationLog...");
      const result = await operationLogService.generateFromOperationLog(
        requestPayload
      );

      console.log("✅ テストターゲット生成結果:", result);

      onResultsGenerated && onResultsGenerated(result);

      onClose();
    } catch (error: any) {
      console.error("テストターゲット生成エラー:", error);
      setError(`テストターゲット生成に失敗しました: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // テンプレート選択変更処理
  const handleTemplateChange = (value: TemplateType) => {
    setTemplateType(value);
    setAdditionalText(TEXT_TEMPLATES[value]);
  };

  // ダイアログクローズ処理
  const handleClose = () => {
    setSelectedFile(null);
    setError(null);
    setLoading(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: "400px" },
      }}
      sx={{
        zIndex: 1500,
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <AutoAwesome color="primary" />
          {mode === "usecase"
            ? "AIでステップを生成"
            : mode === "target"
            ? "AIでターゲットを生成"
            : "AIで生成"}
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box display="flex" flexDirection="column" gap={3}>
          {/* エラー表示（サイズオーバー時は回避策案内リンクも表示） */}
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error === OPERATION_LOG_SIZE_OVER_MESSAGE ? (
                <>
                  {OPERATION_LOG_SIZE_OVER_MESSAGE}{" "}
                  {(() => {
                    const parts = OPERATION_LOG_SIZE_OVER_GUIDANCE_TEXT.split(
                      "{{MANUAL_LINK}}"
                    );
                    return (
                      <>
                        {parts.map((part, i) => (
                          <React.Fragment key={i}>
                            {part}
                            {i < parts.length - 1 && (
                              <Link
                                href={OPERATION_LOG_SIZE_OVER_MANUAL_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ fontWeight: 600 }}
                              >
                                こちら
                              </Link>
                            )}
                          </React.Fragment>
                        ))}
                      </>
                    );
                  })()}
                </>
              ) : (
                error
              )}
            </Alert>
          )}

          {/* 生成モード選択 (modeが未指定の場合のみ表示) */}
          {!mode && (
            <Box>
              <FormControl component="fieldset">
                <FormLabel component="legend">生成モード</FormLabel>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  sx={{ mb: 1 }}
                >
                  操作ログからテスト対象（ターゲット）と、その操作フロー（機能・手順）を生成します。
                  画面の要素だけを洗い出したいときは「ターゲットのみ」、
                  既存ターゲットに対する操作フロー（機能・手順）だけが欲しいときは「機能・手順のみ」、
                  両方をまとめて生成したいときは「ターゲット＋機能・手順」を選択してください。
                </Typography>
                <RadioGroup
                  row
                  value={generationMode}
                  onChange={(e) =>
                    setGenerationMode(
                      e.target.value as "both" | "targets_only" | "usecases_only"
                    )
                  }
                >
                  <FormControlLabel
                    value="both"
                    control={<Radio />}
                    label="ターゲット＋機能・手順"
                  />
                  <FormControlLabel
                    value="targets_only"
                    control={<Radio />}
                    label="ターゲットのみ"
                  />
                  <FormControlLabel
                    value="usecases_only"
                    control={<Radio />}
                    label="機能・手順のみ"
                  />
                </RadioGroup>
              </FormControl>
            </Box>
          )}

          {/* ファイルアップロードセクション */}
          <Box>
            <Typography variant="h6" gutterBottom>
              操作ログファイル
              {operationLogRequired && (
                <Typography component="span" variant="h6" color="error">
                  （必須）
                </Typography>
              )}
            </Typography>

            {/* ドロップゾーン */}
            <Paper
              sx={{
                border: "2px dashed",
                borderColor: dragOver ? "primary.main" : "grey.300",
                backgroundColor: dragOver ? "action.hover" : "background.paper",
                p: 4,
                textAlign: "center",
                cursor: "pointer",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  borderColor: "primary.main",
                  backgroundColor: "action.hover",
                },
              }}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() => document.getElementById("file-input")?.click()}
            >
              <input
                id="file-input"
                type="file"
                accept=".json"
                style={{ display: "none" }}
                onChange={handleFileInputChange}
              />

              {selectedFile ? (
                <Box>
                  <Description
                    sx={{ fontSize: 48, color: "success.main", mb: 1 }}
                  />
                  <Typography variant="h6" color="success.main">
                    {selectedFile.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    サイズ: {(selectedFile.size / 1024).toFixed(1)} KB
                  </Typography>
                </Box>
              ) : (
                <Box>
                  <CloudUpload
                    sx={{ fontSize: 48, color: "text.secondary", mb: 1 }}
                  />
                  <Typography variant="h6" color="text.secondary">
                    JSONファイルをドロップまたはクリックして選択
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    操作ログのJSONファイルを選択してください
                  </Typography>
                </Box>
              )}
            </Paper>
          </Box>

          {mode === "target" && scopeKind === "component" && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                分析対象の PathまたはURL（任意）
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                入力した場合は、操作ログ内でそのPathに該当する画面情報のみを分析対象とします。未入力の場合は操作ログ全体を分析対象とします。
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder='例: /dashboard または https://example.com/app'
                value={componentScopePath}
                onChange={(e) => setComponentScopePath(e.target.value)}
                variant="outlined"
                disabled={loading}
              />
            </Box>
          )}

          {/* テキスト入力セクション */}
          <Box>
            {mode === "target" ? (
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography variant="h6">登録対象情報</Typography>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <Select
                    value={templateType}
                    onChange={(e) =>
                      handleTemplateChange(e.target.value as TemplateType)
                    }
                    disabled={loading}
                    MenuProps={{
                      disablePortal: true,
                      disableScrollLock: true,
                      PaperProps: {
                        sx: {
                          maxHeight: 300,
                          zIndex: 1600,
                        },
                      },
                    }}
                  >
                    {TEMPLATE_OPTIONS.map((option) => (
                      <MenuItem key={option.id} value={option.id}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            ) : (
              <Typography variant="h6" gutterBottom>
                {mode === "usecase"
                  ? "追加の指示（任意）"
                  : "登録対象情報（任意）"}
              </Typography>
            )}
            {mode === "usecase" && (
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                mb={1}
              >
                ステップ生成時にAIに考慮してほしい追加の指示があれば入力してください
              </Typography>
            )}
            {!mode && (
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                mb={1}
              >
                テストターゲットや機能・手順の生成に関する追加情報を入力してください
              </Typography>
            )}
            <TextField
              fullWidth
              multiline
              rows={4}
              placeholder={
                mode === "usecase"
                  ? "例: ログイン後の操作を想定してください\n例: エラーケースも含めてください\n例: 入力値の検証ステップを追加してください"
                  : mode === "target"
                  ? "テストターゲット作成に関する情報を入力してください"
                  : "例: ログイン機能のテスト\n例: ユーザー登録フロー\n例: 決済処理の確認\n例: 特に抽出を強化したいドメインの指定: https://example.com"
              }
              value={additionalText}
              onChange={(e) => setAdditionalText(e.target.value)}
              variant="outlined"
              disabled={loading}
            />
          </Box>

          {/* 情報表示 */}
          <Box>
            {/* NOTE: 対象ビューの表示条件
                - mode="target": ItemSpecificationPanelから開いた「AIでターゲットを生成」の場合のみ表示
                - mode="usecase": UseCaseEditModalから開いた「AIでステップを生成」の場合も表示
                - mode=undefined: TestTargetManagerから開いた「AIで生成」では表示しない
                  （全ビューが対象のため。generationMode="targets_only"を選択しても表示しない） */}
            {viewId && (mode === "target" || mode === "usecase") && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 0.5 }}
              >
                <strong>
                  {scopeKind === "component" ? "登録対象コンポーネント" : "登録対象ビュー"}
                  :
                </strong>{" "}
                {scopeDisplayName || viewName || viewId}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary">
              <strong>ワークスペースID:</strong> {workspaceId || "未設定"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>プロジェクトID:</strong> {projectId || "未設定"}
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          キャンセル
        </Button>
        <Button
          onClick={handleGenerate}
          variant="contained"
          disabled={
            (operationLogRequired && !selectedFile) ||
            (!selectedFile && !additionalText.trim()) ||
            loading ||
            !workspaceId ||
            !projectId
          }
          startIcon={loading ? <CircularProgress size={20} /> : <AutoAwesome />}
        >
          {loading
            ? "生成中..."
            : mode === "usecase"
            ? "ステップを生成"
            : mode === "target"
            ? "テストターゲット生成"
            : generationMode === "both"
            ? "ターゲット＋機能・手順を生成"
            : generationMode === "targets_only"
            ? "ターゲット生成"
            : "機能・手順を生成"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
